/**
 * @typedef {Object} KeyboardListNavigation
 * @property {Function} focusNext - Focuses the next element in the list.
 * @property {Function} focusPrev - Focuses the previous element in the list.
 * @property {Function} teardown - Removes all event listeners and cleans up the object.
 */

/**
 * Initialises keyboard navigation for an element.
 *
 * @param {Object} opts
 * @param {HTMLElement} opts.containerEl - The element to navigate.
 * @param {HTMLElement} [opts.triggerEl] -
 *  The element that triggers the container to open.
 * @param {(event: KeyboardEvent) => Boolean | undefined} [opts.handle] -
 *  A function to call when an arrow key is pressed. If the function returns
 *  `true`, the event is considered handledand nothing more will happen. If it
 *  returns `false` or `undefined`, activate the default behaviour of the
 *  navigation.
 * @param {Boolean} [opts.wrap] -
 *  When `true`, the arrows cycle within the items (past the last item wraps to the first and vice versa).
 *  When `false` (default), stepping back from the first item returns to `triggerEl` instead of wrapping.
 * @returns {KeyboardListNavigation} - The initialised keyboard navigation object.
 *
 * The items are taken out of the tab order (`tabindex="-1"`), so Tab skips past the whole list in one step
 * instead of walking every item. The arrow keys move focus between them instead.
 */
export function initListNavigation(opts) {
  const { containerEl, triggerEl = null, handle = null, wrap = false } = opts;

  let teardownStack = [];

  const [focusPrev, focusNext] = getFocusFunctions(containerEl, triggerEl, wrap);

  const items = getFocusableChildren(containerEl);
  items.forEach((item) => item.setAttribute("tabindex", "-1"));
  teardownStack.unshift(() => items.forEach((item) => item.removeAttribute("tabindex")));

  const keydownHandler = (event) => {
    if (handle?.(event)) return;

    // Only plain arrows navigate; a modifier (e.g. Alt+Arrow for browser back/forward) keeps its native meaning.
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    // Stop the handled arrow from bubbling to an outer list navigation (e.g. a parent menu of a nested dropdown).
    if (event.key === "ArrowUp") {
      focusPrev();
      event.stopPropagation();
      return;
    }

    if (event.key === "ArrowDown") {
      focusNext();
      event.stopPropagation();
      return;
    }
  };

  containerEl.addEventListener("keydown", preventBrowserScroll);
  containerEl.addEventListener("keydown", keydownHandler, { passive: true });

  teardownStack.unshift(() => {
    containerEl.removeEventListener("keydown", preventBrowserScroll);
    containerEl.removeEventListener("keydown", keydownHandler);
  });

  if (triggerEl !== null && !containerEl.contains(triggerEl)) {
    triggerEl.addEventListener("keydown", preventBrowserScroll);
    triggerEl.addEventListener("keydown", keydownHandler, { passive: true });

    teardownStack.unshift(() => {
      triggerEl.removeEventListener("keydown", preventBrowserScroll);
      triggerEl.removeEventListener("keydown", keydownHandler);
    });
  }

  return {
    focusNext,
    focusPrev,
    teardown() {
      teardownStack.forEach((func) => func());
    },
  };
}

function getFocusFunctions(containerEl, triggerEl, wrap) {
  const focusPrev = () => {
    if (document.activeElement === triggerEl) {
      getLastFocusableElement(containerEl)?.focus();
    } else {
      // When wrapping (menus), cycle among items; otherwise step back to the trigger, e.g. an autocomplete input.
      const beforeFirst = wrap ? null : triggerEl;
      const focusedEl = getPreviousFocusable(containerEl) ?? beforeFirst ?? getLastFocusableElement(containerEl);
      focusedEl?.focus();
    }
  };

  const focusNext = () => {
    if (document.activeElement === triggerEl) {
      getFirstFocusableElement(containerEl)?.focus();
    } else {
      const focusedEl = getNextFocusableElement(containerEl) ?? getFirstFocusableElement(containerEl);
      focusedEl?.focus();
    }
  };

  return [focusPrev, focusNext];
}

// `:scope > *` maches all direct children of the queried element.
// Only match direct children to avoid matching elements that could be in
// a dropdown, popup, toast, etc.
const FOCUSABLE_ELEMENTS_SELECTOR = [
  ":scope > .button:not(:disabled)",
  ":scope > .dropdown > .button:not(:disabled)",
  // In the `<.input>` component, the `.checkable-container` is always
  // within a container with `has-flex has-flex-col` classes
  ":scope > .has-flex.has-flex-col > .checkable-container > input:not([type='hidden']):not(:disabled)",
  ":scope > .accordion__item > .accordion__header",
].join(",");

export function focusFirst(containerEl) {
  getFirstFocusableElement(containerEl)?.focus();
}

function getFirstFocusableElement(containerEl) {
  const focusableElements = getFocusableChildren(containerEl);
  return focusableElements[0] ?? null;
}

function getLastFocusableElement(containerEl) {
  const focusableElements = getFocusableChildren(containerEl);
  return focusableElements[focusableElements.length - 1] ?? null;
}

function getPreviousFocusable(containerEl) {
  const focusableElements = getFocusableChildren(containerEl);
  const activeElementIndex = focusableElements.indexOf(document.activeElement);
  return focusableElements[activeElementIndex - 1] ?? null;
}

function getNextFocusableElement(containerEl) {
  const focusableElements = getFocusableChildren(containerEl);
  const activeElementIndex = focusableElements.indexOf(document.activeElement);
  return focusableElements[activeElementIndex + 1] ?? null;
}

function getFocusableChildren(containerEl) {
  const nodeList = containerEl.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
  const elements = Array.from(nodeList);
  return filterVisibleElements(elements);
}

const filterVisibleElements =
  "checkVisibility" in HTMLElement.prototype
    ? (elements) => elements.filter((el) => el.checkVisibility())
    : // Fallback to an incomplete filter for older browsers.
      // This will not work for elements that are hidden by a parent element.
      (elements) =>
        elements.filter(
          (el) => el.style.display !== "none" && el.style.visibility !== "hidden" && el.type !== "hidden" && !el.disabled
        );

function preventBrowserScroll(event) {
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
  }
}
